import React, { useState, useEffect, useRef } from 'react';
import './InstallPrompt.css';

const DISMISSED_KEY = 'installSheetDismissed';

const DownloadIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
    <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"></path>
    <polyline points="7 10 12 15 17 10"></polyline>
    <line x1="12" y1="15" x2="12" y2="3"></line>
  </svg>
);

export default function InstallPrompt() {
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [isInstalled, setIsInstalled] = useState(false);
  const [isInstallable, setIsInstallable] = useState(false);
  const [isIOS, setIsIOS] = useState(false);
  const [dismissed, setDismissed] = useState(
    () => localStorage.getItem(DISMISSED_KEY) === 'true'
  );
  const [showIOSInstructions, setShowIOSInstructions] = useState(false);
  const promptReceived = useRef(false);

  useEffect(() => {
    let isMounted = true;
    const ua = window.navigator.userAgent.toLowerCase();
    const isIOSDevice = /iphone|ipad|ipod/.test(ua);
    const isAndroidDevice = /android/.test(ua);
    if (isMounted) setIsIOS(isIOSDevice);

    const isStandalone =
      window.matchMedia('(display-mode: standalone)').matches ||
      window.navigator.standalone ||
      document.referrer.includes('android-app://');

    if (isStandalone) {
      if (isMounted) setIsInstalled(true);
      localStorage.setItem('appInstalled', 'true');
      return;
    }

    if (localStorage.getItem('appInstalled') === 'true') {
      if (isMounted) setIsInstalled(true);
      return;
    }

    const handlePrompt = (e) => {
      e.preventDefault();
      promptReceived.current = true;
      if (isMounted) {
        setDeferredPrompt(e);
        setIsInstallable(true);
      }
    };

    const handleInstalled = () => {
      if (isMounted) {
        setIsInstalled(true);
        setIsInstallable(false);
        setDeferredPrompt(null);
        localStorage.setItem('appInstalled', 'true');
      }
    };

    window.addEventListener('beforeinstallprompt', handlePrompt);
    window.addEventListener('appinstalled', handleInstalled);

    if (isIOSDevice && isMounted) setIsInstallable(true);

    // Android fallback: if the native prompt hasn't fired after 2s, still show UI
    const timeout = setTimeout(() => {
      if (isMounted && isAndroidDevice && !promptReceived.current) {
        setIsInstallable(true);
      }
    }, 2000);

    return () => {
      isMounted = false;
      clearTimeout(timeout);
      window.removeEventListener('beforeinstallprompt', handlePrompt);
      window.removeEventListener('appinstalled', handleInstalled);
    };
  }, []);

  const handleInstall = async () => {
    if (isIOS) {
      setShowIOSInstructions(true);
      return;
    }
    if (!deferredPrompt) return;
    try {
      deferredPrompt.prompt();
      const { outcome } = await deferredPrompt.userChoice;
      if (outcome === 'accepted') {
        setIsInstalled(true);
        localStorage.setItem('appInstalled', 'true');
      }
      setDeferredPrompt(null);
    } catch (err) {
      console.error('Install error:', err);
    }
  };

  const handleDismiss = () => {
    setDismissed(true);
    localStorage.setItem(DISMISSED_KEY, 'true');
  };

  if (isInstalled || !isInstallable) return null;

  return (
    <>
      {!dismissed && (
        <div className="install-sheet">
          <div className="install-sheet-header">
            <div className="install-sheet-text">
              <p className="install-sheet-title">Add VU Connect to your home screen</p>
              <p className="install-sheet-desc">Quick access to courses, events, and campus resources.</p>
            </div>
            <button className="install-sheet-close" onClick={handleDismiss} aria-label="Dismiss">
              <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <line x1="18" y1="6" x2="6" y2="18"></line>
                <line x1="6" y1="6" x2="18" y2="18"></line>
              </svg>
            </button>
          </div>
          <button className="install-sheet-cta" onClick={handleInstall}>Install App</button>
          <button className="install-sheet-dismiss" onClick={handleDismiss}>Not now</button>
        </div>
      )}

      {dismissed && (
        <button className="install-fab" onClick={handleInstall} aria-label="Install VU Connect">
          <DownloadIcon />
        </button>
      )}

      {showIOSInstructions && (
        <div
          className="ip-ios-backdrop"
          onClick={(e) => { if (e.target === e.currentTarget) setShowIOSInstructions(false); }}
        >
          <div className="ip-ios-modal">
            <div className="ip-ios-header">
              <h3>Add to your iPhone</h3>
              <button className="ip-ios-close" onClick={() => setShowIOSInstructions(false)}>
                <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <line x1="18" y1="6" x2="6" y2="18"></line>
                  <line x1="6" y1="6" x2="18" y2="18"></line>
                </svg>
              </button>
            </div>
            <div className="ip-ios-body">
              <div className="ip-ios-steps">
                <div className="ip-ios-step">
                  <div className="ip-ios-step-num">1</div>
                  <div className="ip-ios-step-content">
                    <p>Tap the <strong>Share button</strong></p>
                    <div className="ip-ios-icon-box">
                      <svg xmlns="http://www.w3.org/2000/svg" width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <path d="M4 12v8a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2v-8"></path>
                        <polyline points="16 6 12 2 8 6"></polyline>
                        <line x1="12" y1="2" x2="12" y2="15"></line>
                      </svg>
                    </div>
                  </div>
                </div>
                <div className="ip-ios-step">
                  <div className="ip-ios-step-num">2</div>
                  <div className="ip-ios-step-content">
                    <p>Tap <strong>"Add to Home Screen"</strong></p>
                    <div className="ip-ios-icon-box">
                      <svg xmlns="http://www.w3.org/2000/svg" width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <rect x="3" y="3" width="18" height="18" rx="2" ry="2"></rect>
                        <line x1="12" y1="8" x2="12" y2="16"></line>
                        <line x1="8" y1="12" x2="16" y2="12"></line>
                      </svg>
                    </div>
                  </div>
                </div>
                <div className="ip-ios-step">
                  <div className="ip-ios-step-num">3</div>
                  <div className="ip-ios-step-content">
                    <p>Tap <strong>"Add"</strong> in the top right</p>
                    <div className="ip-ios-icon-box">
                      <svg xmlns="http://www.w3.org/2000/svg" width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <polyline points="20 6 9 17 4 12"></polyline>
                      </svg>
                    </div>
                  </div>
                </div>
              </div>
            </div>
            <div className="ip-ios-footer">
              <button className="ip-ios-btn" onClick={() => setShowIOSInstructions(false)}>
                Got it!
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
